import MaterialIcon from '@components/common/MaterialIcon';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import { TRAIN_AI_PAGE_ROUTE } from '@constants/routes';
import type {
  IntelligenceQuestionDTO,
  IntelligenceTopicDTO,
} from '@conversed/rest/src/contracts/home/home.dto';
import { Sentiment } from '@conversed/shared/src/central/constants/sentiment';
import { purify } from '@helpers/domHelper';
import { useModal } from '@hooks/modal/useModal';
import { useHasTrainAIAccess } from '@hooks/useHasTrainAIAccess';
import parse from 'html-react-parser';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { SENTIMENT_DISPLAY } from '../sentimentDisplay';
import { IntelligenceSentimentBar } from './IntelligenceSentimentBar';
import { SentimentChip } from './SentimentChip';

export type TIntelligenceTopicModalProps = {
  topic: IntelligenceTopicDTO;
  questions: IntelligenceQuestionDTO[];
};

export const IntelligenceTopicModal: React.FC<TIntelligenceTopicModalProps> = ({
  topic,
  questions,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hideModal } = useModal();
  const showTrainAILink = useHasTrainAIAccess();

  const goToTrainAI = () => {
    hideModal();
    navigate(TRAIN_AI_PAGE_ROUTE);
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-start gap-8'>
        <div className='flex flex-col'>
          <span className='text-pico font-medium uppercase tracking-wider text-gray-400'>
            {t('home.v2.intelligence.topic-modal.total-questions')}
          </span>
          <span className='text-3xl font-bold text-gray-800'>
            {topic.volume}
          </span>
        </div>

        <div className='flex flex-1 flex-col gap-0.5'>
          <span className='text-pico font-medium uppercase tracking-wider text-gray-400'>
            {t('home.v2.intelligence.topic-modal.sentiment')}
          </span>
          <div className='flex items-center justify-between text-timestamp font-normal text-gray-300'>
            <span className='flex items-center gap-0.5'>
              <MaterialIcon
                icon='arrow_back'
                classes={['text-pico', '!leading-none']}
              />
              <MaterialIcon
                icon={SENTIMENT_DISPLAY[Sentiment.Unhappy].icon}
                type='outlined'
                classes={['text-tiny', '!leading-none']}
              />
              {t('sentiment.unhappy')}
            </span>
            <span className='flex items-center gap-0.5'>
              <MaterialIcon
                icon={SENTIMENT_DISPLAY[Sentiment.Neutral].icon}
                type='outlined'
                classes={['text-tiny', '!leading-none']}
              />
              {t('sentiment.neutral')}
            </span>
            <span className='flex items-center gap-0.5'>
              <MaterialIcon
                icon={SENTIMENT_DISPLAY[Sentiment.Happy].icon}
                type='outlined'
                classes={['text-tiny', '!leading-none']}
              />
              {t('sentiment.happy')}
              <MaterialIcon
                icon='arrow_forward'
                classes={['text-pico', '!leading-none']}
              />
            </span>
          </div>
          <IntelligenceSentimentBar score={topic.sentimentScore} />
        </div>
      </div>

      <div className='border-t border-gray-200 pt-4'>
        <p className='mb-3 text-sm font-medium text-gray-700'>
          {t('home.v2.intelligence.topic-modal.questions-label')}
        </p>

        {questions.length === 0 ? (
          <p className='py-6 text-center text-sm text-gray-400'>
            {t('home.v2.intelligence.topic-modal.no-questions')}
          </p>
        ) : (
          <ul className='flex max-h-[50dvh] flex-col gap-2 overflow-y-auto pr-1'>
            {questions.map((question) => (
              <li
                key={question._id}
                className='flex flex-col gap-0.5 rounded-lg border border-gray-200 bg-white px-3 py-2.5'>
                <div className='flex items-start justify-between gap-3'>
                  <span className='flex items-start gap-2'>
                    <MaterialIcon
                      icon='person'
                      type='outlined'
                      classes={[
                        'text-sm',
                        'text-gray-400',
                        'mt-0.5',
                        '!leading-none',
                      ]}
                    />
                    <span className='text-tiny text-gray-700'>
                      {question.question}
                    </span>
                  </span>
                  <SentimentChip sentiment={question.sentiment} size='sm' />
                </div>
                {question.answer && (
                  <div className='flex items-start gap-2'>
                    <MaterialIcon
                      icon='smart_toy'
                      type='outlined'
                      classes={[
                        'text-sm',
                        'text-primary-base',
                        'mt-0.5',
                        '!leading-none',
                      ]}
                    />
                    <span className='text-tiny text-gray-400 formatted-html-content'>
                      {parse(purify(question.answer))}
                    </span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className='flex items-center justify-between border-t border-gray-200 pt-4'>
        <ButtonT
          type='button'
          styling='outline'
          size='xs'
          rounding='md'
          onClick={hideModal}>
          {t('general.buttons.close')}
        </ButtonT>
        {showTrainAILink && (
          <ButtonT
            type='button'
            styling='primary'
            size='xs'
            rounding='md'
            onClick={goToTrainAI}>
            <span className='flex items-center gap-1'>
              {t('home.v2.intelligence.topic-modal.train-ai')}
              <MaterialIcon icon='arrow_forward' classes={['text-sm']} />
            </span>
          </ButtonT>
        )}
      </div>
    </div>
  );
};
